import React from 'react';
import { AlertTriangle, CalendarClock, Shield } from 'lucide-react';
import { StatusBadge } from '../UI/StatusBadge';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import { yearEndExpiryWarning } from './AnnualLeaveYearEndBalance';

const URGENCY_STYLES = {
  info: {
    wrap: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-600',
    title: 'text-blue-900',
    body: 'text-blue-800',
    Icon: CalendarClock,
  },
  warning: {
    wrap: 'bg-amber-50 border-amber-200',
    icon: 'text-amber-600',
    title: 'text-amber-900',
    body: 'text-amber-800',
    Icon: AlertTriangle,
  },
  urgent: {
    wrap: 'bg-red-50 border-red-200',
    icon: 'text-red-600',
    title: 'text-red-900',
    body: 'text-red-800',
    Icon: Shield,
  },
};

const formatDays = (value) => formatLeaveDays(value ?? 0);

const bookedClause = (plan) => {
  const futureApprovedDays = Number(plan.future_approved_al_days || 0);
  if (futureApprovedDays <= 0) return '';
  return ` Approved leave not yet taken (${formatDays(futureApprovedDays)} ${pluralizeDays(futureApprovedDays)}) is already counted.`;
};

const AnnualLeavePlanningCallout = ({ plan }) => {
  if (!plan?.visible || plan.risk_band === 'safe') return null;

  const urgency = URGENCY_STYLES[plan.urgency] || URGENCY_STYLES.warning;
  const Icon = urgency.Icon;
  const pendingDays = Number(plan.pending_al_days || 0);
  const forfeiture = Number(plan.projected_forfeiture || 0);
  const projectedUnused = formatDays(plan.projected_unused_al);
  const capValue = Number(plan.carry_forward_cap || 0);
  const capDays = `${formatDays(capValue)} ${pluralizeDays(capValue)}`;
  const destYear = plan.destination_year;
  const sourceYear = plan.source_year;
  const recommended = Number(plan.recommended_book_days || 0);
  const pendingDayLabel = `${formatDays(pendingDays)} ${pluralizeDays(pendingDays)}`;
  const forfeitureDayLabel = `${formatDays(forfeiture)} ${pluralizeDays(forfeiture)}`;
  const expiryWarning = yearEndExpiryWarning({
    remainingLabel: forfeitureDayLabel,
    sourceYear,
    destinationYear: destYear,
  });
  const pendingExpiryWarning = yearEndExpiryWarning({
    remainingLabel: forfeitureDayLabel,
    sourceYear,
    destinationYear: destYear,
    pending: true,
  });

  let title = `${forfeitureDayLabel} above the ${sourceYear} Annual Leave carry-forward limit`;
  let body = `Your projected unused Annual Leave as at 31 December ${sourceYear} is ${projectedUnused} days. Only ${capDays} can carry into ${destYear}. ${expiryWarning}.`;
  if (recommended > 0) {
    body += ` Take at least ${formatDays(plan.recommended_book_days)} ${pluralizeDays(recommended)} by 31 December.`;
  }
  body += bookedClause(plan);

  if (plan.risk_band === 'safe') {
    title = `${projectedUnused} days projected as at 31 December ${sourceYear}`;
    body = `All ${projectedUnused} days can carry into ${destYear}. Nothing is forfeited if you take no more Annual Leave.${bookedClause(plan)}`;
  } else if (plan.risk_band === 'pending_dependent') {
    title = `Within the ${sourceYear} Annual Leave carry-forward limit if pending leave is approved`;
    body = `Your projected unused Annual Leave as at 31 December ${sourceYear} is ${projectedUnused} days against the ${capDays} limit. Your ${pendingDayLabel} of pending Annual Leave would cover the excess if approved. ${pendingExpiryWarning}.${bookedClause(plan)}`;
  } else if (plan.is_december) {
    title = `${forfeitureDayLabel} must be used by 31 Dec ${sourceYear}`;
  }

  return (
    <div
      role="alert"
      className={`rounded-xl border p-4 flex items-start gap-3 ${urgency.wrap}`}
    >
      <Icon className={`w-5 h-5 ${urgency.icon} mt-0.5 flex-shrink-0`} aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className={`text-sm font-semibold ${urgency.title}`}>{title}</h2>
          <StatusBadge status={plan.risk_band} />
        </div>
        <p className={`text-sm mt-0.5 ${urgency.body}`}>{body}</p>
      </div>
    </div>
  );
};

export default AnnualLeavePlanningCallout;
